import React, { useEffect, useRef, useState } from "react";
import { unset } from "lodash";
import axios from "axios";

import { FormHelper, ValidationHelper } from "../helpers";
import { Form, Formik, useFormikContext } from "formik";
import { Alert, SubmitButton } from "../components";
import { Button } from "../components/ui";
// Status classes (dot, Saving…/error tones, Retry link, reduced-motion floor)
// are the Field primitive's — same visual language as per-field auto-save.
import "../components/ui/Field.scss";
import "./ajax-autosave.scss";

// Formats a nested Formik errors object into a flat list of "field: message"
// strings so we can show which required fields the user missed.
const flattenFormikErrors = (errors, prefix = "") => {
  const out = [];
  if (!errors || typeof errors !== "object") return out;
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof val === "string") {
      out.push({ path, message: val });
    } else if (Array.isArray(val)) {
      val.forEach((v, i) => {
        if (typeof v === "string") out.push({ path: `${path}[${i}]`, message: v });
        else if (v && typeof v === "object") {
          out.push(...flattenFormikErrors(v, `${path}[${i}]`));
        }
      });
    } else if (val && typeof val === "object") {
      out.push(...flattenFormikErrors(val, path));
    }
  }
  return out;
};

// Auto-scroll to the first invalid field + banner after a failed submit.
// Lives inside <Formik> so it sees submitCount and the errors object live.
const FormValidationFeedback = () => {
  const { errors, submitCount, isValid } = useFormikContext();
  const containerRef = useRef(null);
  const lastHandledSubmit = useRef(0);

  useEffect(() => {
    if (submitCount === 0 || submitCount === lastHandledSubmit.current) return;
    if (isValid) return;

    lastHandledSubmit.current = submitCount;

    // Defer a tick so React flushes the new error UI first.
    const timer = setTimeout(() => {
      const banner = containerRef.current;
      if (banner) banner.scrollIntoView({ behavior: "smooth", block: "center" });
      // Also try to focus the first invalid input, if any.
      const firstInvalid = document.querySelector(
        '[aria-invalid="true"], .has-error input, .has-error select, .has-error textarea'
      );
      if (firstInvalid && typeof firstInvalid.focus === "function") {
        firstInvalid.focus({ preventScroll: true });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [submitCount, isValid]);

  const flat = flattenFormikErrors(errors);
  if (submitCount === 0 || isValid || flat.length === 0) return null;

  // Humanise field paths: "product.brand_id" → "Product Brand Id".
  const humanise = (path) =>
    path
      .replace(/\[\d+\]/g, "")
      .replace(/_/g, " ")
      .replace(/\./g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());

  return (
    <div ref={containerRef} className="ajax-form__validation-banner" role="alert">
      <Alert
        type="error"
        message="Please fix the highlighted fields before saving"
        description={
          <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
            {flat.slice(0, 8).map((e) => (
              <li key={e.path}>
                <strong>{humanise(e.path)}</strong>: {e.message}
              </li>
            ))}
            {flat.length > 8 && (
              <li><em>…and {flat.length - 8} more field(s)</em></li>
            )}
          </ul>
        }
      />
    </div>
  );
};

// Fontaine M56B — form-level auto-save (the RC no-Save-button rule). Rendered
// inside <Formik> when `autoSave` is on: every values change re-arms a
// debounce; when it fires and the form validates, the SAME submit path runs —
// same URL, method and full payload the Save button used to send. A validation
// failure saves nothing: the field-level errors stay inline and the next edit
// re-arms the timer. An in-flight save defers the next one instead of racing it.
const AjaxFormAutosave = ({ debounce = 600, onBlocked }) => {
  const formik = useFormikContext();
  const snapshot = JSON.stringify(formik.values);
  const committed = useRef(snapshot);
  // The debounce closure must see the live Formik bag, not the render it was
  // scheduled in (isSubmitting flips between the keystroke and the timer).
  const live = useRef(formik);
  live.current = formik;

  useEffect(() => {
    if (snapshot === committed.current) return undefined;

    let timer;
    const fire = async () => {
      if (live.current.isSubmitting) {
        timer = setTimeout(fire, 250);
        return;
      }
      const errors = await live.current.validateForm();
      if (Object.keys(errors).length > 0) {
        // Saves nothing — the fields show their inline errors, and the caller
        // (e.g. the listing form's per-section badge) can say so quietly.
        onBlocked?.(errors);
        return;
      }
      committed.current = JSON.stringify(live.current.values);
      live.current.submitForm();
    };
    timer = setTimeout(fire, debounce);
    return () => clearTimeout(timer);
  }, [snapshot, debounce]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const AjaxForm = ({
  afterSubmit,
  children,
  enableReinitialize = false,
  allowFormReset = false,
  fields = [],
  footer,
  initialValues,
  method,
  requiredFields,
  secondButton,
  submitAction,
  submitLabel = "Save",
  secondarySubmitButtonLabel,
  submitClassName,
  submitContainerClassName,
  successTitle,
  successMessage,
  url,
  token,
  processSubmitValues,
  validationSchema,
  showFooter = true,
  excludeFields = [],
  showErrorNextToSubmit = false,
  onCancel,
  checkQuota,
  autoSave = false,
  autoSaveDebounce = 600,
  // Optional: hear about save failures even when the form hides the built-in
  // status row (showFooter=false) — the listing form's per-section badge.
  onAutoSaveError,
}) => {
  const [status, setStatus] = useState("input");
  const [errorMsg, setErrorMsg] = useState("");

  const defaultProcessValues = (values) => {
    return values;
  };

  const processValues = processSubmitValues || defaultProcessValues;

  const defaultSubmitAction = async (values) => {
    if (url === false) {
      return true;
    }

    if (typeof checkQuota === "function") {
      const allowed = await checkQuota();

      if (!allowed) {
        return;
      }
    }

    try {
      const { data } = await axios[method](url, processValues(values), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      return data;
    } catch (err) {
      throw err?.response?.data;
    }
  };

  submitAction = submitAction || defaultSubmitAction;

  const fail = (error) => {
    setStatus("error");
    // Laravel 422 responses carry { message, errors: { field: [msgs] } }.
    // Flatten the errors map so the team can see which field is actually failing.
    let msg;
    if (error?.errors && typeof error.errors === "object") {
      const lines = Object.entries(error.errors)
        .flatMap(([field, msgs]) =>
          (Array.isArray(msgs) ? msgs : [msgs]).map((m) => `${field}: ${m}`)
        )
        .join(" | ");
      msg = lines || error?.message;
    } else {
      msg = error?.message;
    }
    setErrorMsg(msg);
    onAutoSaveError?.(msg);
  };

  const onSubmit = async (values) => {
    let result;

    try {
      for (const path of excludeFields) {
        unset(values, path);
      }

      result = await submitAction(values);
    } catch (error) {
      if (!autoSave) {
        fail(error);
        return;
      }
      // Auto-save retries once by itself (same values, same payload) before
      // surfacing the quiet inline error + Retry.
      try {
        result = await submitAction(values);
      } catch (secondError) {
        fail(secondError);
        return;
      }
    }

    afterSubmit && afterSubmit(result, values.__submitType === "secondary");
    setStatus("success");

    return result;
  };

  requiredFields = requiredFields || fields;
  initialValues = initialValues || FormHelper.initialValues(fields);
  validationSchema =
    validationSchema || ValidationHelper.schema(requiredFields);

  const formProps = {
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize,
  };

  return (
    <Formik {...formProps}>
      {({ isSubmitting, ...props }) => {
        return (
          <Form>
            {/* Auto-save must not lock the inputs mid-typing, and its
                feedback is the quiet status row, not banner alerts. */}
            <fieldset disabled={autoSave ? false : isSubmitting}>
              {autoSave && (
                <AjaxFormAutosave
                  debounce={autoSaveDebounce}
                  onBlocked={
                    onAutoSaveError
                      ? () => onAutoSaveError("Couldn't save — fix the highlighted fields.")
                      : undefined
                  }
                />
              )}

              {!autoSave && successTitle && status === "success" && (
                <Alert
                  type="success"
                  message={successTitle}
                  description={successMessage}
                />
              )}

              {!autoSave && status === "error" && (
                <Alert
                  type="error"
                  message={
                    errorMsg ||
                    "An error occurred while processing your request."
                  }
                />
              )}

              {/* Rendered in auto-save mode too: it only appears after a MANUAL
                  submit attempt (keyed on submitCount, which blocked auto-saves
                  never increment) — so clicking Update on an invalid form still
                  explains itself instead of silently doing nothing. */}
              <FormValidationFeedback />

              {typeof children === "function" ? children(props) : children}

              {footer && footer({ isSubmitting })}

              {/* Auto-save replaces the Save button with a status row:
                  Saving… → All changes saved, error + Retry inline. Any
                  `secondButton` (transactional commands, e.g. the admin
                  actions dropdown) keeps its place. */}
              {!footer && showFooter && autoSave && (
                <div className="ajax-form__autosave-row">
                  {secondButton && secondButton}

                  {(isSubmitting || status !== "input") && (
                    <span
                      className={`ft-field__status is-${
                        isSubmitting ? "saving" : status === "error" ? "error" : "saved"
                      }`}
                      role="status"
                      aria-live="polite"
                    >
                      <span className="ft-field__status-dot" aria-hidden="true" />
                      {isSubmitting
                        ? "Saving…"
                        : status === "error"
                          ? errorMsg || "Couldn't save"
                          : "All changes saved"}
                      {!isSubmitting && status === "error" && (
                        <button
                          type="button"
                          className="ft-field__retry"
                          onClick={() => props.submitForm()}
                        >
                          Retry
                        </button>
                      )}
                    </span>
                  )}
                </div>
              )}

              {!footer && showFooter && !autoSave && (
                <div className={`action-row ${submitContainerClassName}`}>
                  {secondButton && secondButton}

                  {allowFormReset && (
                    <SubmitButton
                      label="Clear all"
                      variant="secondary"
                      data-btn="form-clear"
                      onClick={() => {
                        props.setValues({ values: {} });
                        submitAction();
                      }}
                      className="mr-2"
                    />
                  )}

                  {showErrorNextToSubmit &&
                    props.submitCount > 0 &&
                    !props.isValid && (
                      <span className="error-next-to-submit">
                        Field missing
                      </span>
                    )}

                  {onCancel && (
                    <Button variant="secondary" size="lg" className="mr-2" data-btn="form-cancel" onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                  {secondarySubmitButtonLabel && (
                    <SubmitButton
                      label={secondarySubmitButtonLabel}
                      variant="secondary"
                      data-btn="form-save-secondary"
                      isSubmitting={isSubmitting}
                      className={submitClassName + " mr-2"}
                      onClick={() => {
                        props.setFieldValue("__submitType", "secondary");
                        props.submitForm();
                      }}
                    />
                  )}
                  <SubmitButton
                    label={submitLabel}
                    isSubmitting={isSubmitting}
                    className={submitClassName}
                    data-btn="form-save"
                  />
                </div>
              )}
            </fieldset>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AjaxForm;
